import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query"

import { getPlatformGifts, getUserGifts, purchasePlatformGift } from "@/shared/api"
import { isFeatureEnabled } from "@/shared/features"
import { ApiError } from "@/shared/api/client"
import type {
  PlatformGiftPurchaseErrorCode,
  PlatformGiftsParams,
  PlatformGiftsResponse,
  PurchasePlatformGiftResponse,
  UserGift,
  UserGiftsParams,
  UserGiftsResponse,
} from "@/shared/api/types"

export const INVENTORY_GIFTS_PAGE_LIMIT = 50

/** Full owned gifts — single cache for inventory, bootstrap prefetch, and WS updates. */
export const INVENTORY_GIFTS_PARAMS: UserGiftsParams = {
  limit: INVENTORY_GIFTS_PAGE_LIMIT,
}

/** Client-side filter for betting UI — excludes round-held and Telegram transfer hold. */
export function filterBettableGifts(items: UserGift[]): UserGift[] {
  return items.filter((g) => g.available_for_bet !== false)
}

export const giftKeys = {
  all: ["gifts"] as const,
  list: (params: UserGiftsParams) => [...giftKeys.all, "list", params] as const,
}

export const useUserGifts = (
  params: UserGiftsParams = INVENTORY_GIFTS_PARAMS,
  options?: Omit<
    UseQueryOptions<UserGiftsResponse>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery<UserGiftsResponse>({
    queryKey: giftKeys.list(params),
    queryFn: () => getUserGifts(params),
    staleTime: 30_000,
    ...options,
    enabled: isFeatureEnabled("gifts") && (options?.enabled ?? true),
  })
}

// ── Marketplace (platform gifts for sale) ────────────────────────────────────

export const MARKETPLACE_GIFTS_PAGE_LIMIT = 50

/** Canonical request params for the marketplace list — shared cache key. */
export const MARKETPLACE_GIFTS_PARAMS: PlatformGiftsParams = {
  limit: MARKETPLACE_GIFTS_PAGE_LIMIT,
}

export const marketplaceKeys = {
  all: ["marketplace-gifts"] as const,
  list: (params: PlatformGiftsParams) =>
    [...marketplaceKeys.all, "list", params] as const,
}

/**
 * Marketplace gifts list.
 * - Always refetches on mount so the user sees fresh prices when they open the tab.
 * - Cached data shows immediately while the refetch runs (no flicker for return visits).
 * - Cache lives for the whole session (`gcTime: Infinity`) so navigating away and
 *   back doesn't drop it.
 * - When there's no cache yet, `isLoading` is true → callers render the skeleton.
 */
export const usePlatformGifts = (
  params: PlatformGiftsParams = MARKETPLACE_GIFTS_PARAMS,
  options?: Omit<
    UseQueryOptions<PlatformGiftsResponse>,
    "queryKey" | "queryFn"
  >
) =>
  useQuery<PlatformGiftsResponse>({
    queryKey: marketplaceKeys.list(params),
    queryFn: () => getPlatformGifts(params),
    refetchOnMount: "always",
    gcTime: Number.POSITIVE_INFINITY,
    staleTime: 0,
    ...options,
    enabled: isFeatureEnabled("gifts") && (options?.enabled ?? true),
  })

/** Purchase-time backend codes that mean our marketplace list is out of date. */
const MARKETPLACE_STALE_ERROR_CODES = new Set<PlatformGiftPurchaseErrorCode>([
  "platform_gift_price_changed",
  "gift_not_available_on_platform",
  "gift_not_for_sale",
  "gift_not_found",
])

function readApiErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null
  const d = err.details
  if (d && typeof d === "object" && "code" in d) {
    const code = (d as { code?: unknown }).code
    return typeof code === "string" ? code : null
  }
  return null
}

interface PurchaseVars {
  externalGiftId: string
  /** Price the user saw in the storefront, nanotons. */
  expectedPriceNanoTon: number
}

/**
 * Buy a platform gift. On success or on a "list is stale" 400 (price changed /
 * gift already sold / no longer for sale), invalidates the marketplace list so
 * the storefront re-fetches.
 */
export const usePurchasePlatformGift = () => {
  const queryClient = useQueryClient()
  return useMutation<PurchasePlatformGiftResponse, unknown, PurchaseVars>({
    mutationFn: ({ externalGiftId, expectedPriceNanoTon }) =>
      purchasePlatformGift(externalGiftId, {
        expected_price_nano_ton: expectedPriceNanoTon,
      }),
    onSuccess: () => {
      // Bought gift left the marketplace and joined the user's inventory.
      void queryClient.invalidateQueries({ queryKey: marketplaceKeys.all })
      void queryClient.invalidateQueries({ queryKey: giftKeys.all })
    },
    onError: (error) => {
      const code = readApiErrorCode(error)
      if (code && MARKETPLACE_STALE_ERROR_CODES.has(code as PlatformGiftPurchaseErrorCode)) {
        void queryClient.invalidateQueries({ queryKey: marketplaceKeys.all })
      }
    },
  })
}
